import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Download, Copy, Grid, Table, X, Filter } from 'lucide-react';
import { useScan } from '@/contexts/ScanContext';
import { useAnalytics } from '@/services/analytics';
import { DomainScanResult } from '@/types';
import { AdvancedFilterOptions, BrandRiskAnalysis as BrandRiskData } from '../../../shared/types';
import { scanApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn, formatDateTime, getStatusColor, getStatusIcon } from '@/lib/utils';
import ValuationBadge from './ValuationBadge';
import DomainValuation from './DomainValuation';
import BrandRiskAnalysis from './BrandRiskAnalysis';
import BrandRiskPill from './BrandRiskPill';
import { useBrandRiskQueue } from '@/hooks/useBrandRiskQueue';
import { usePriceChanges } from '@/hooks/usePriceChanges';
import AdvancedFilters from './AdvancedFilters';
import GridSkeleton from './GridSkeleton';
import TableSkeleton from './TableSkeleton';
import ResultsTable from './ResultsTable';
import WatchButton from './WatchButton';
import { useWatchlistContext } from '@/contexts/WatchlistContext';

type SortField = 'domain' | 'status' | 'scannedAt' | 'valuation' | 'price';
type SortOrder = 'asc' | 'desc';
type ViewMode = 'grid' | 'table';

const splitDomain = (domain: string) => {
  const idx = domain.indexOf('.');
  if (idx === -1) return { base: domain, tld: '' };
  return { base: domain.slice(0, idx), tld: domain.slice(idx) };
};

interface ResultsGridProps {
  onViewModeChange?: (mode: ViewMode) => void;
  initialViewMode?: ViewMode;
}

const ResultsGrid: React.FC<ResultsGridProps> = ({
  onViewModeChange,
  initialViewMode = 'table'
}) => {
  const { state } = useScan();
  const analytics = useAnalytics();
  useWatchlistContext(); // subscribes to watchlist state for WatchButton re-renders
  const [watchToast, setWatchToast] = useState<string | null>(null);
  const showWatchToast = (msg: string) => {
    setWatchToast(msg);
    setTimeout(() => setWatchToast(null), 3000);
  };
  const [viewMode, setViewMode] = useState<ViewMode>(initialViewMode);
  const [page, setPage] = useState(1); // 1-based for explicit pagination
  const [rowsPerPage] = useState(24); // 24 for grid (4x6)
  const [sortField, setSortField] = useState<SortField>('scannedAt');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | 'available' | 'registered' | 'error'>('');
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilterOptions>({});
  const [selectedResult, setSelectedResult] = useState<DomainScanResult | null>(null);
  const [showValuationModal, setShowValuationModal] = useState(false);
  const [showBrandRiskModal, setShowBrandRiskModal] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [valuationResult, setValuationResult] = useState<DomainScanResult | null>(null);
  const [brandRiskResult, setBrandRiskResult] = useState<DomainScanResult | null>(null);
  const onBrandRiskResult = useCallback((domain: string, analysis: BrandRiskData) => {
    setAllResults(prev => prev.map(r => r.domain === domain ? { ...r, brandRisk: analysis } : r));
    setBrandRiskResult(prev => prev?.domain === domain ? { ...prev, brandRisk: analysis } : prev);
  }, []);
  const { queue: queueBrandRisk, markVisible, retry: retryBrandRisk, getStatus: getBrandRiskStatus, getResult: getBrandRiskResult } = useBrandRiskQueue(onBrandRiskResult);
  const [allResults, setAllResults] = useState<DomainScanResult[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [rateLimitedUntil, setRateLimitedUntil] = useState<number | null>(null);
  const [rateLimitMessage, setRateLimitMessage] = useState<string | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const lastRequestKeyRef = useRef<string>('');
  const debounceTimerRef = useRef<number | null>(null);

  // Determine scanning state for UI gating (place early to avoid TDZ)
  const isScanningUi = useMemo(() => {
    const percent = state.progress?.current || 0;
    return state.isLoading || state.progress?.isScanning || (percent > 0 && percent < 100);
  }, [state.isLoading, state.progress]);

  // Price change badges — fetch once the scan is done, for available domains only
  const availableDomainsForPriceCheck = useMemo(
    () => allResults.filter(r => r.status === 'available').map(r => r.domain),
    [allResults]
  );
  const priceChanges = usePriceChanges(availableDomainsForPriceCheck, !isScanningUi);

  // whether we are using API-backed pagination (no in-memory results)
  const isApiMode = useMemo(() => (state.results || []).length === 0, [state.results]);

  // Load results from API when filters change
  const loadResults = useCallback(async (pageNum: number = 1, append: boolean = false) => {
    if (isLoading && !append) return;
    const now = Date.now();
    if (rateLimitedUntil && now < rateLimitedUntil) return;

    // Create a comprehensive filter object
    const filters: AdvancedFilterOptions = {
      status: statusFilter || undefined,
      search: searchTerm || undefined,
      page: pageNum,
      limit: rowsPerPage,
      ...advancedFilters
    };

    const reqKey = [pageNum, rowsPerPage, statusFilter || '', searchTerm || '', JSON.stringify(advancedFilters)].join('|');
    if (!append && lastRequestKeyRef.current === reqKey) {
      return; // skip duplicate same-params request
    }
    lastRequestKeyRef.current = reqKey;

    setIsLoading(true);
    try {
      const response = await scanApi.getResults(filters);

      if (append) {
        setAllResults(prev => [...prev, ...(response.data || [])]);
      } else {
        setAllResults(response.data || []);
      }
      setTotalResults(response.pagination?.total || 0);
      setRateLimitMessage(null);
    } catch (error: any) {
      console.error('Error loading results:', error);
      if (error?.code === 'RATE_LIMIT' || error?.status === 429) {
        const cooldownMs = 30000; // 30s
        setRateLimitedUntil(Date.now() + cooldownMs);
        setRateLimitMessage('Too many requests. Please wait a few seconds and try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, searchTerm, advancedFilters, rowsPerPage, isLoading, rateLimitedUntil]);

  // Update allResults when state.results changes (for real-time updates)
  useEffect(() => {
    if (Array.isArray(state.results) && state.results.length > 0) {
      setAllResults(state.results);
      setTotalResults(state.results.length);
    }
  }, [state.results]);

  // Initial load (single source of truth) when using API mode
  useEffect(() => {
    if ((state.results || []).length === 0) {
      loadResults(1, false);
    }
  }, [loadResults, state.results]);

  // Base dataset: prefer API-loaded results if present, otherwise use in-memory scan results
  const baseResults = allResults.length > 0 ? allResults : (state.results || []);

  // Apply client-side filtering and sorting to the loaded dataset
  const filteredSortedResults = useMemo(() => {
    let results = Array.isArray(baseResults) ? [...baseResults] : [];

    // Filter by search term
    if (searchTerm) {
      const needle = searchTerm.toLowerCase();
      results = results.filter(r => r.domain.toLowerCase().includes(needle));
    }

    // Filter by status
    if (statusFilter) {
      results = results.filter(r => r.status === statusFilter);
    }

    // Apply advanced filters
    if (advancedFilters.regex) {
      try {
        const regex = new RegExp(advancedFilters.regex);
        results = results.filter(r => regex.test(r.domain));
      } catch (error) {
        // Invalid regex, skip this filter
      }
    }

    if (advancedFilters.booleanQuery) {
      // For boolean query, we'll do a simple text search since we don't have full-text search on client
      const query = advancedFilters.booleanQuery.toLowerCase();
      const terms = query.split(/\s+(?:AND|OR|NOT)\s+/);
      results = results.filter(r => {
        const domain = r.domain.toLowerCase();
        return terms.some(term => domain.includes(term));
      });
    }

    if (advancedFilters.tlds && advancedFilters.tlds.length > 0) {
      results = results.filter(r =>
        advancedFilters.tlds!.some(tld => r.domain.endsWith(tld))
      );
    }

    if (advancedFilters.lengthMin !== undefined) {
      results = results.filter(r => r.domain.length >= advancedFilters.lengthMin!);
    }

    if (advancedFilters.lengthMax !== undefined) {
      results = results.filter(r => r.domain.length <= advancedFilters.lengthMax!);
    }

    if (advancedFilters.registrar) {
      results = results.filter(r =>
        r.whoisData?.registrar?.toLowerCase().includes(advancedFilters.registrar!.toLowerCase())
      );
    }

    if (advancedFilters.dateFrom) {
      results = results.filter(r => new Date(r.scannedAt) >= advancedFilters.dateFrom!);
    }

    if (advancedFilters.dateTo) {
      results = results.filter(r => new Date(r.scannedAt) <= advancedFilters.dateTo!);
    }

    if (advancedFilters.valuationMin !== undefined) {
      results = results.filter(r =>
        (r.valuation?.estimatedValue?.min || 0) >= advancedFilters.valuationMin!
      );
    }

    if (advancedFilters.valuationMax !== undefined) {
      results = results.filter(r =>
        (r.valuation?.estimatedValue?.min || 0) <= advancedFilters.valuationMax!
      );
    }

    // Apply pricing filters
    if (advancedFilters.priceMin !== undefined) {
      results = results.filter(r =>
        r.pricing?.purchasePrice !== null && r.pricing!.purchasePrice! >= advancedFilters.priceMin!
      );
    }

    if (advancedFilters.priceMax !== undefined) {
      results = results.filter(r =>
        r.pricing?.purchasePrice !== null && r.pricing!.purchasePrice! <= advancedFilters.priceMax!
      );
    }

    if (advancedFilters.excludeUnavailablePricing) {
      results = results.filter(r =>
        r.pricing?.purchasePrice !== null && r.pricing!.purchasePrice !== undefined
      );
    }

    if (advancedFilters.premiumOnly) {
      results = results.filter(r =>
        r.pricing?.premium === true
      );
    }

    if (advancedFilters.purchasableOnly) {
      results = results.filter(r =>
        r.pricing?.purchasable === true
      );
    }

    // Sort
    results.sort((a, b) => {
      // Prioritize searched domain - always put it first
      if (state.searchedDomain) {
        const aIsSearched = a.domain === state.searchedDomain;
        const bIsSearched = b.domain === state.searchedDomain;

        if (aIsSearched && !bIsSearched) return -1;
        if (!aIsSearched && bIsSearched) return 1;
      }

      let aVal: any;
      let bVal: any;
      switch (sortField) {
        case 'domain':
          aVal = a.domain.toLowerCase();
          bVal = b.domain.toLowerCase();
          break;
        case 'status':
          aVal = a.status;
          bVal = b.status;
          break;
        case 'valuation':
          aVal = a.valuation?.estimatedValue?.min || 0;
          bVal = b.valuation?.estimatedValue?.min || 0;
          break;
        case 'price':
          aVal = a.pricing?.purchasePrice || 0;
          bVal = b.pricing?.purchasePrice || 0;
          break;
        case 'scannedAt':
        default:
          aVal = new Date(a.scannedAt).getTime();
          bVal = new Date(b.scannedAt).getTime();
          break;
      }
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return results;
  }, [baseResults, searchTerm, statusFilter, advancedFilters, sortField, sortOrder]);

  // isApiMode defined above
  const totalPages = useMemo(() => {
    if (isApiMode) {
      return Math.max(1, Math.ceil(totalResults / rowsPerPage));
    }
    // client-side total based on filtered list
    return Math.max(1, Math.ceil(filteredSortedResults.length / rowsPerPage));
  }, [isApiMode, totalResults, rowsPerPage, filteredSortedResults.length]);

  const handleViewModeChange = useCallback((mode: ViewMode) => {
    setViewMode(mode);
    onViewModeChange?.(mode);
    analytics.domainFiltered('view_mode', mode, filteredSortedResults.length);
  }, [onViewModeChange, analytics, filteredSortedResults.length]);

  // Explicit pagination handlers (Prev/Next)
  const goToPage = useCallback((nextPage: number) => {
    if (nextPage < 1 || nextPage > totalPages) return;
    setPage(nextPage);
    if (isApiMode) {
      // fetch the requested page from API
      loadResults(nextPage, false);
    }
  }, [totalPages, isApiMode, loadResults]);

  // Reset and reload when filters change (only if no real-time results), debounced
  useEffect(() => {
    setPage(1);
    if ((state.results || []).length === 0) {
      setAllResults([]);
      if (debounceTimerRef.current) {
        window.clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = window.setTimeout(() => {
        loadResults(1, false);
      }, 250);
    }
  }, [searchTerm, statusFilter, advancedFilters, sortField, sortOrder, loadResults, state.results]);

  // Handle advanced filter changes
  const handleAdvancedFiltersChange = useCallback((newFilters: AdvancedFilterOptions) => {
    setAdvancedFilters(newFilters);
  }, []);

  // Handle clear all filters
  const handleClearAllFilters = useCallback(() => {
    setSearchTerm('');
    setStatusFilter('');
    setAdvancedFilters({});
  }, []);

  // Auto-queue available domains for brand risk analysis when results change
  useEffect(() => {
    allResults.forEach(result => {
      if (result.status === 'available') {
        queueBrandRisk(result.domain);
      }
    });
  }, [allResults, queueBrandRisk]);

  const handleViewBrandRisk = useCallback((result: DomainScanResult) => {
    const latestResult = allResults.find(r => r.id === result.id) || result;
    setBrandRiskResult(latestResult);
    setShowBrandRiskModal(true);
  }, [allResults]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (advancedFilters.search) count++;
    if (advancedFilters.regex) count++;
    if (advancedFilters.booleanQuery) count++;
    if (advancedFilters.tlds && advancedFilters.tlds.length > 0) count++;
    if (advancedFilters.lengthMin !== undefined || advancedFilters.lengthMax !== undefined) count++;
    if (advancedFilters.valuationMin !== undefined || advancedFilters.valuationMax !== undefined) count++;
    if (advancedFilters.registrar) count++;
    if (advancedFilters.dateFrom || advancedFilters.dateTo) count++;
    return count;
  }, [advancedFilters]);

  // isScanningUi defined above

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      analytics.domainCopied(text, false);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const exportResults = (format: 'csv' | 'json') => {
    const data = filteredSortedResults.map(result => ({
      domain: result.domain,
      status: result.status,
      scannedAt: result.scannedAt,
      valuation: result.valuation?.estimatedValue?.min || 0,
      registrar: result.whoisData?.registrar || '',
      creationDate: result.whoisData?.creationDate || '',
      expirationDate: result.whoisData?.expirationDate || '',
    }));

    if (format === 'csv') {
      const headers = ['Domain', 'Status', 'Scanned At', 'Valuation', 'Registrar', 'Creation Date', 'Expiration Date'];
      const csvContent = [
        headers.join(','),
        ...data.map(row => Object.values(row).map(val => `"${val}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `domain-scan-results-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } else {
      const jsonContent = JSON.stringify(data, null, 2);
      const blob = new Blob([jsonContent], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `domain-scan-results-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const hasAnyResults = useMemo(() => {
    const stateCount = Array.isArray(state.results) ? state.results.length : 0;
    return (stateCount > 0) || (allResults.length > 0);
  }, [state.results, allResults.length]);

  if (!hasAnyResults && !state.isLoading && !isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center py-20">
        <div className="text-center">
          <Search className="h-12 w-12 mx-auto text-gray-200 mb-4" />
          <h3 className="text-base font-medium text-gray-900 mb-1">No results yet</h3>
          <p className="text-sm text-gray-400">Start by scanning a domain using the input above</p>
        </div>
      </div>
    );
  }

  if ((state.isLoading || isLoading) && !hasAnyResults) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex-1 overflow-y-auto">
          {viewMode === 'grid' ? <GridSkeleton count={12} /> : <TableSkeleton rows={10} />}
        </div>
      </div>
    );
  }

  // If table view is selected, render the existing table component
  if (viewMode === 'table') {
    return <ResultsTable onViewModeChange={handleViewModeChange} currentViewMode={'table'} />;
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header with controls */}
      <div className="px-4 py-3 border-b border-gray-100 bg-white">
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="text-sm font-semibold text-gray-900 flex-shrink-0">
            Results ({filteredSortedResults.length}{totalResults > filteredSortedResults.length && allResults.length > 0 ? ` of ${totalResults}` : ''})
          </h3>
          <div className="flex gap-1.5 flex-shrink-0">
            <button onClick={() => exportResults('csv')} className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1 transition-colors">
              <Download className="h-3 w-3" />CSV
            </button>
            <button onClick={() => exportResults('json')} className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1 transition-colors">
              <Download className="h-3 w-3" />JSON
            </button>
          </div>

          <div className="flex-1 flex items-center gap-2 min-w-0">
            <div className="relative flex-1 min-w-[120px]">
              <Search className="absolute left-2.5 top-1/2 -trangray-y-1/2 text-gray-400 h-3.5 w-3.5" />
              <Input
                placeholder="Search domains..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={isScanningUi}
                className="pl-8 h-8 text-sm border-gray-200"
              />
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {rateLimitMessage && (
              <span className="hidden sm:inline text-xs text-amber-600">{rateLimitMessage}</span>
            )}
            {!isScanningUi && (
              <button
                onClick={() => setShowAdvancedFilters(true)}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded px-2 py-1 transition-colors"
              >
                <Filter className="h-3.5 w-3.5" />
                Advanced
                {activeFilterCount > 0 && <span className="bg-blue-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{activeFilterCount}</span>}
              </button>
            )}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              disabled={isScanningUi}
              className="h-8 px-2 border border-gray-200 rounded text-sm text-gray-700 bg-white"
            >
              <option value="">All Status</option>
              <option value="available">Available</option>
              <option value="registered">Registered</option>
              <option value="error">Error</option>
            </select>
            <select
              value={`${sortField}_${sortOrder}`}
              onChange={(e) => {
                const [field, order] = e.target.value.split('_') as [SortField, SortOrder];
                setSortField(field);
                setSortOrder(order);
                analytics.domainFiltered('sort', `${field}_${order}`, filteredSortedResults.length);
                if (field === 'valuation') {
                  analytics.valuationColumnSorted(order, filteredSortedResults.length);
                }
              }}
              disabled={isScanningUi}
              className="h-8 px-2 border border-gray-200 rounded text-sm text-gray-700 bg-white"
            >
              <option value="scannedAt_desc">Newest First</option>
              <option value="scannedAt_asc">Oldest First</option>
              <option value="domain_asc">Domain A-Z</option>
              <option value="domain_desc">Domain Z-A</option>
              <option value="status_asc">Status A-Z</option>
              <option value="status_desc">Status Z-A</option>
              <option value="valuation_desc">Highest Value</option>
              <option value="valuation_asc">Lowest Value</option>
              <option value="price_desc">Highest Price</option>
              <option value="price_asc">Lowest Price</option>
            </select>

            {/* View toggle */}
            <div className="flex border border-gray-200 rounded overflow-hidden">
              <button
                onClick={() => handleViewModeChange('grid')}
                className="h-8 px-2.5 flex items-center transition-colors bg-blue-500 text-white"
              >
                <Grid className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => handleViewModeChange('table')}
                className="h-8 px-2.5 flex items-center transition-colors bg-white text-gray-400 hover:text-gray-600"
              >
                <Table className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Grid Content */}
      <div className="flex-1 overflow-y-auto" ref={scrollContainerRef}>
        {isScanningUi && (
          <p className="px-4 pt-2 text-xs text-gray-400">Filtering available after scan completes.</p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 p-4">
          {(isApiMode
            ? filteredSortedResults
            : filteredSortedResults.slice((page - 1) * rowsPerPage, page * rowsPerPage)
          ).map((result) => {
            const isSearchedDomain = state.searchedDomain === result.domain;
            const { base, tld } = splitDomain(result.domain);
            const price = result.pricing?.purchasePrice;
            const priceChange = priceChanges.get(result.domain);
            return (
              <div
                key={result.id}
                className={`group border rounded-lg p-4 bg-white transition-colors ${
                  isSearchedDomain
                    ? 'border-blue-300 ring-1 ring-blue-200'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {/* Domain + Watch */}
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h4 className="font-mono text-sm leading-snug min-w-0 truncate">
                    <span className="text-gray-400 font-medium">{base}</span>
                    <span className="font-bold text-gray-900">{tld}</span>
                  </h4>
                  <WatchButton
                    domain={result.domain}
                    snapshot={{
                      available: result.status === 'available' ? true : result.status === 'registered' ? false : null,
                      priceUsd: price ?? null,
                      registrar: result.pricing?.registrar ?? result.whoisData?.registrar ?? null,
                      valuation: result.valuation?.estimatedValue?.min ?? null,
                      brandRiskState:
                        result.brandRisk?.riskLevel === 'low' ? 'clear' :
                        result.brandRisk?.riskLevel === 'medium' ? 'caution' :
                        result.brandRisk?.riskLevel === 'high' ? 'risk' : 'unknown',
                    }}
                    onToast={showWatchToast}
                  />
                </div>

                {/* Status + Price */}
                <div className="flex items-center justify-between mb-2.5">
                  <span className={`text-xs font-medium ${
                    result.status === 'available' ? 'text-green-600' :
                    result.status === 'registered' ? 'text-gray-400' : 'text-amber-500'
                  }`}>
                    {result.status === 'available' ? '✓ Available' :
                     result.status === 'registered' ? 'Taken' : 'Error'}
                  </span>
                  {price != null && (
                    <span className="text-sm font-semibold text-gray-900">${price.toFixed(2)}</span>
                  )}
                </div>

                {/* Brand risk */}
                <div className="mb-2.5">
                  <BrandRiskPill
                    domain={result.domain}
                    isAvailable={result.status === 'available'}
                    status={getBrandRiskStatus(result.domain)}
                    result={getBrandRiskResult(result.domain)}
                    onVisible={markVisible}
                    onViewRisk={() => handleViewBrandRisk(result)}
                    onRetry={retryBrandRisk}
                  />
                </div>

                {/* Price change badge */}
                {priceChange?.changeLabel && (
                  <p className={`text-xs mb-2 ${(priceChange.changePercent ?? 0) < 0 ? 'text-green-600' : 'text-red-500'}`}>
                    {priceChange.changeLabel}
                  </p>
                )}

                {/* Footer: copy + details */}
                <div className="flex items-center justify-between pt-2 border-t border-gray-100 mt-1">
                  <button
                    onClick={() => copyToClipboard(result.domain)}
                    className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1 transition-colors"
                  >
                    <Copy className="h-3 w-3" />
                    Copy
                  </button>
                  <Link
                    to={`/domain/${encodeURIComponent(result.domain)}`}
                    className="text-xs text-gray-400 hover:text-blue-500 transition-colors"
                  >
                    View details →
                  </Link>
                </div>
              </div>
            );
          })}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 pb-4 flex items-center justify-between">
            <span className="text-xs text-gray-400">Page {page} of {totalPages}</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => goToPage(page - 1)}
                disabled={page <= 1 || isLoading || !!rateLimitedUntil}
                className="text-xs px-3 py-1.5 border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-40 transition-colors"
              >
                Previous
              </button>
              <button
                onClick={() => goToPage(page + 1)}
                disabled={page >= totalPages || isLoading || !!rateLimitedUntil}
                className="text-xs px-3 py-1.5 border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-40 transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Domain Details Modal */}
      {selectedResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Domain Details</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedResult(null)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-lg">{selectedResult.domain}</h3>
                  <Badge className={cn("mt-2", getStatusColor(selectedResult.status))}>
                    {getStatusIcon(selectedResult.status)}
                    <span className="ml-1 capitalize">{selectedResult.status}</span>
                  </Badge>
                </div>

                {selectedResult.valuation && (
                  <div>
                    <h4 className="font-medium mb-2">Valuation</h4>
                    <div
                      className="cursor-pointer"
                      onClick={() => {
                        setValuationResult(selectedResult);
                        setShowValuationModal(true);
                      }}
                    >
                      <ValuationBadge valuation={selectedResult.valuation} />
                    </div>
                  </div>
                )}

                {selectedResult.whoisData && (
                  <div>
                    <h4 className="font-medium mb-2">WHOIS Data</h4>
                    <div className="bg-gray-50 p-3 rounded-lg space-y-1 text-sm">
                      {selectedResult.whoisData.registrar && (
                        <div><span className="font-medium">Registrar:</span> {selectedResult.whoisData.registrar}</div>
                      )}
                      {selectedResult.whoisData.creationDate && (
                        <div><span className="font-medium">Created:</span> {new Date(selectedResult.whoisData.creationDate).toLocaleString()}</div>
                      )}
                      {selectedResult.whoisData.expirationDate && (
                        <div><span className="font-medium">Expires:</span> {new Date(selectedResult.whoisData.expirationDate).toLocaleString()}</div>
                      )}
                      {selectedResult.whoisData.nameServers && selectedResult.whoisData.nameServers.length > 0 && (
                        <div>
                          <span className="font-medium">Name Servers:</span>
                          <ul className="ml-4 mt-1">
                            {selectedResult.whoisData.nameServers.map((ns, index) => (
                              <li key={index} className="text-xs">{ns}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {selectedResult.dnsData && (
                  <div>
                    <h4 className="font-medium mb-2">DNS Data</h4>
                    <div className="bg-gray-50 p-3 rounded-lg space-y-1 text-sm">
                      {selectedResult.dnsData.ipAddresses && selectedResult.dnsData.ipAddresses.length > 0 && (
                        <div>
                          <span className="font-medium">IP Addresses:</span>
                          <ul className="ml-4 mt-1">
                            {selectedResult.dnsData.ipAddresses.map((ip, index) => (
                              <li key={index} className="text-xs">{ip}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {selectedResult.dnsData.mxRecords && selectedResult.dnsData.mxRecords.length > 0 && (
                        <div>
                          <span className="font-medium">MX Records:</span>
                          <ul className="ml-4 mt-1">
                            {selectedResult.dnsData.mxRecords.map((mx, index) => (
                              <li key={index} className="text-xs">{mx}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="text-sm text-gray-600">
                  <span className="font-medium">Scanned:</span> {formatDateTime(selectedResult.scannedAt)}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Valuation Modal */}
      {showValuationModal && valuationResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Smart Valuation</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowValuationModal(false);
                    setValuationResult(null);
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <DomainValuation
                valuation={valuationResult.valuation}
                domain={valuationResult.domain}
              />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Brand Risk Modal */}
      {showBrandRiskModal && brandRiskResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowBrandRiskModal(false);
                    setBrandRiskResult(null);
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <BrandRiskAnalysis
                key={`${brandRiskResult.domain}-${brandRiskResult.brandRisk?.analyzedAt || 'no-analysis'}`}
                brandRisk={brandRiskResult.brandRisk}
                domain={brandRiskResult.domain}
                onAnalysisComplete={(analysis) => {
                  const updatedResult = { ...brandRiskResult, brandRisk: analysis };
                  setBrandRiskResult(updatedResult);
                  setAllResults(prev => prev.map(r => r.id === brandRiskResult.id ? updatedResult : r));
                }}
              />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Advanced Filters Modal */}
      {showAdvancedFilters && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Advanced Filters</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAdvancedFilters(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <AdvancedFilters
                filters={advancedFilters}
                onFiltersChange={handleAdvancedFiltersChange}
                onClearFilters={handleClearAllFilters}
              />
              <div className="flex justify-end space-x-2 mt-6 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setShowAdvancedFilters(false)}
                >
                  Close
                </Button>
                <Button
                  onClick={() => setShowAdvancedFilters(false)}
                >
                  Apply Filters
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Watch toast */}
      {watchToast && (
        <div className="fixed bottom-6 left-1/2 -trangray-x-1/2 z-50 bg-gray-900 text-white text-sm font-medium px-4 py-2.5 rounded-lg shadow-lg pointer-events-none">
          {watchToast}
        </div>
      )}
    </div>
  );
};

export default ResultsGrid;
